import {
  AGENT_NAME_FORCED_IDEATION,
  AGENT_NAME_MARKET_SIZING,
  AGENT_NAME_TECH_EXPLORATION,
  AGENT_TOP_ROUTE_NAME_FORCED_IDEATION,
  AGENT_TOP_ROUTE_NAME_MARKET_SIZING,
  AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
} from '@/constants';
import { IconTypes } from '@stockmarkteam/donguri-ui';
import type {
  InsightRecommendedAction,
  InsightRecommendedActionAgentType,
} from '@/types/insights';

export type InsightAgentPresentation = {
  label: string;
  icon: IconTypes;
  linkIcon: IconTypes;
  routeName: string;
  opensInNewTab: boolean;
  trackingEventName:
    | 'insight_tech_exploration_open'
    | 'insight_survey_open'
    | 'insight_forced_ideation_open'
    | 'insight_market_sizing_open';
};

export const insightAgentPresentations: Record<
  InsightRecommendedActionAgentType,
  InsightAgentPresentation
> = {
  tech_exploration: {
    label: AGENT_NAME_TECH_EXPLORATION,
    icon: 'search',
    linkIcon: 'external-link',
    routeName: AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
    opensInNewTab: true,
    trackingEventName: 'insight_tech_exploration_open',
  },
  survey: {
    label: 'スレッド作成',
    icon: 'sparkles-fill',
    linkIcon: 'chevron-right',
    routeName: 'surveyTop',
    opensInNewTab: false,
    trackingEventName: 'insight_survey_open',
  },
  forced_ideation: {
    label: AGENT_NAME_FORCED_IDEATION,
    icon: 'idea',
    linkIcon: 'external-link',
    routeName: AGENT_TOP_ROUTE_NAME_FORCED_IDEATION,
    opensInNewTab: true,
    trackingEventName: 'insight_forced_ideation_open',
  },
  market_sizing: {
    label: AGENT_NAME_MARKET_SIZING,
    icon: 'market-size',
    linkIcon: 'external-link',
    routeName: AGENT_TOP_ROUTE_NAME_MARKET_SIZING,
    opensInNewTab: true,
    trackingEventName: 'insight_market_sizing_open',
  },
};

export type InsightActionCell = {
  action: InsightRecommendedAction;
  column: 'agent' | 'survey';
  row: number;
};

/**
 * 推奨アクションを「スレッド作成以外は左列、スレッド作成は右列」に振り分け、
 * 行順（左 → 右 → 次の行）に並べて返す。
 * 横の情報は左から右に流れるので、左列が空のまま右だけに置かない。
 * 列ごとにまとめて DOM に出すと Tab のフォーカス順が見た目の行順とずれるため、
 * 描画側はこの順のまま 1 つのループで出力する。
 */
export const arrangeInsightActions = (
  actions: InsightRecommendedAction[],
): InsightActionCell[] => {
  const agents = actions.filter(action => action.agent_type !== 'survey');
  const survey = actions.filter(action => action.agent_type === 'survey');
  if (agents.length === 0) {
    return survey.map((action, index) => ({
      action,
      column: 'agent',
      row: index + 1,
    }));
  }
  const rowCount = Math.max(agents.length, survey.length);

  return Array.from({ length: rowCount }, (_, index) => index + 1).flatMap(
    row =>
      [
        { action: agents[row - 1], column: 'agent' as const, row },
        { action: survey[row - 1], column: 'survey' as const, row },
      ].filter((cell): cell is InsightActionCell => cell.action !== undefined),
  );
};
